import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, TrendingUp, DollarSign, Clock, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { UpliftLogo, UpliftLogoWhite } from '../components/UpliftLogo';

const BizPlanLanding = () => {
  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: 'AI-Powered Generation',
      description: 'Get a complete business plan in minutes, not weeks. Our AI understands your industry.',
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Investor-Ready Format',
      description: 'Professional formatting that banks and investors expect to see.',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Financial Projections',
      description: 'Realistic 5-year projections based on industry benchmarks.',
    },
  ];

  const useCases = [
    'Bank Loans & SBA Loans',
    'Investor Pitches',
    'Grant Applications',
    'Strategic Planning',
    'Partnership Proposals',
    'Franchise Applications',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2" data-testid="uplift-home-link">
                <UpliftLogo size="sm" showText={false} />
              </Link>
              <Link to="/bizplanai" className="flex items-center gap-2" data-testid="bizplan-logo">
                <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  BizPlanAI
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="bizplan-login-btn">Log in</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="bizplan-get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Business Plans
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Professional Business Plans in <span className="text-emerald-600">Minutes</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Stop paying consultants $2,000+. Get an investor-ready business plan powered by AI for a fraction of the cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200" data-testid="bizplan-hero-cta">
                  Create Your Plan - $49
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/bizplanai/pricing">
                <Button size="lg" variant="outline" data-testid="bizplan-view-pricing">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              No subscription required • Pay per plan • Instant download
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-emerald-400 font-medium mb-8">PERFECT FOR</p>
          <div className="flex flex-wrap justify-center gap-4">
            {useCases.map((useCase, index) => (
              <span
                key={index}
                className="px-6 py-3 rounded-full bg-slate-800 text-white font-medium"
              >
                {useCase}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need to Impress
            </h2>
            <p className="text-lg text-slate-600">
              Our AI creates comprehensive business plans that get results
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-xl transition-all border border-slate-200/50"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What's Included
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Every business plan includes all the sections banks and investors need to see.
              </p>
              <div className="space-y-4">
                {[
                  'Executive Summary',
                  'Market Analysis & Research',
                  'Competitive Analysis',
                  'Marketing Strategy',
                  'Financial Projections',
                  'Funding Request & Use of Funds',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">Save $1,900+ vs consultants</span>
                </div>
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">Ready in under 5 minutes</span>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">Professional PDF download</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-emerald-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Launch Your Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Join entrepreneurs who are getting funded with BizPlanAI
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 shadow-xl" data-testid="bizplan-cta-bottom">
              Create Your Business Plan
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <UpliftLogoWhite size="md" showText={true} className="mb-4" />
              <p className="text-slate-400 text-sm max-w-md">
                Uplift helps you rise in every area that matters. 
                Career tools, business planning, and credit repair - all in one place.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/careerboost" className="text-slate-400 hover:text-white">CareerBoost</Link></li>
                <li><Link to="/bizplanai" className="text-slate-400 hover:text-white">BizPlanAI</Link></li>
                <li><Link to="/creditboost" className="text-slate-400 hover:text-white">CreditBoost</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-400 hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-slate-400 text-sm">
                  Founded by <span className="text-white font-medium">Robert Elder III</span>, CEO
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  © 2024-2025 Uplift III / Robert Elder III. All Rights Reserved.
                </p>
              </div>
              <p className="text-slate-600 text-xs text-center md:text-right max-w-md">
                Uplift™, CareerBoost™, BizPlanAI™, and CreditBoost™ are trademarks of Robert Elder III. 
                Unauthorized copying is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BizPlanLanding;
